<!-- eslint-disable @typescript-eslint/no-unused-expressions -->
<docs>
  # ui-picture
  basic support of ui-picture
  plus support of some helpers for api etc and be able to use webp, srcset, sizes...
</docs>
<script lang="ts">
import { templateRef, until } from '@vueuse/core';
import scrollStore from 'components/SmoothScroll/store';
import { motion } from 'config';
import { ScrollTrigger } from 'vendors/gsap';
import { computed, defineComponent, nextTick, onMounted, PropType, ref, toRefs } from 'vue';

const UiPicture = defineComponent({
  name: 'UiPicture',
  props: {
    src: {
      type: String,
      required: true,
      default: undefined,
    },
    alt: {
      type: String,
      // required: true
      default: null,
    },
    sizes: {
      type: String,
      // required: true
      default: null,
    },
    width: {
      type: [Number, String],
      // required: true
      default: null,
    },
    height: {
      type: [Number, String],
      // required: true,
      default: undefined,
    },
    srcset: {
      type: Object as PropType<Image["srcset"]>,
      default: undefined,
    },
    cover: {
      type: String,
      default: 'contain',
    },
    focalPoint: {
      type: Object as PropType<Point>,
      default: undefined,
    },
    /* please ensure to set false to 1st image (hero) */
    lazy: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: undefined,
    },
    parallax: {
      type: Boolean,
      default: false,
    },
    parallaxSpeed: {
      type: Number,
      default: 0.1,
    },
    parallaxDirection: {
      type: Number,
      default: 1, // 1=y -> -1=x
    },
    forceVisible: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { scrollable } = toRefs(scrollStore.state);

    const el = templateRef<HTMLElement | null>('el', null)
    const img = templateRef<HTMLElement | null>('img', null)
    const visible = ref(!props.lazy);
    const loaded = ref(false);

    if(props.forceVisible) visible.value = true
    /**
     * @return focalPointPoint object
     */
    const position = computed(() => {
      const focalPoint = props.focalPoint || { x: 0.5, y: 0.5 };
      return `${focalPoint.x * 100}% ${focalPoint.y * 100}%`;
    })

    const mainSrc = computed(() => {
      if (props.srcset?.default) {
        return props.srcset.default.split(' ')[0] || props.src;
      }
      return props.src;
    });

    onMounted(() => {
      const setLoaded = () => {
        loaded.value = true
      }

      const imgEl = img.value as HTMLImageElement
      imgEl?.complete
        ? setLoaded()
        : img.value?.addEventListener('load', setLoaded)
    })

    const lazyPlaceholder = computed(() => {
      if (!motion || !props.lazy) {
        return undefined
      }
      // if (loaded.value && visible.value) {
      //   return undefined
      // }
      if (props.placeholder === undefined) {
        return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${props.width} ${props.height}"><rect fill="%23ccc" width="${props.width}" height="${props.height}"/></svg>`
      }
      return props.placeholder
    })


    const initSt = async () => {
      if (motion && props.lazy) {
        await until(scrollable).toBe(true)
        const st = ScrollTrigger.create({
          // markers: true,
          trigger: el.value,
          start: 'top bottom',
          end: 'bottom top',
          onToggle: ({ isActive }: { isActive: boolean }) => {
            if(!props.forceVisible) visible.value = isActive
            if (isActive) {
              nextTick(() => { st.kill(false) }) // only 1time ?
            }
          }
        })
        // force display image without motion
      } else if (!motion) {
        visible.value = true;
      }
    }
    initSt();

    return {
      mainSrc,
      position,
      visible,
      loaded,
      lazyPlaceholder,
    }
  },
});
export default UiPicture
</script>

<template>
  <div ref="el"
       class="ui-picture wrapper -block"
       :data-cover="cover"
       :data-loaded="loaded"
       :data-visible="visible"
       :data-lazy="lazy"
       :data-parallax="parallax"
       :data-placeholder="lazyPlaceholder"
  >
    <!--
      many divs for clean integration...
      if just bloc - placeholder fill all content
      if inline-bloc - adds some gap after depending on line-height - + grow factor anim etc...
      so .ui-picture block - .inside inline-block
     -->
    <div class="container -inline">
      <component :is="parallax ? 'ui-parallax' : 'picture'"
                 tag="picture"
                 :speed="parallaxSpeed"
                 :direction="parallaxDirection"
                 class="picture"
      >
        <template v-if="visible || loaded">
          <source v-if="srcset?.webp"
                  key="webp"
                  type="image/webp"
                  :srcset="srcset.webp"
                  :sizes="sizes"
                  media="(min-width:100px)"
          >
          <source v-if="srcset?.default"
                  key="standard"
                  :srcset="srcset.default"
                  :sizes="sizes"
                  media="(min-width:100px)"
          >
        </template>
        <img ref="img"
             class="img"
             v-bind="{
               src: visible || loaded ? mainSrc : undefined,
               ['data-src']: !visible ? mainSrc : undefined
             }"
             :alt="alt"
             :sizes="sizes"
             :width="width"
             :height="height"
             :loading="lazy ? 'lazy' : undefined"
        >
      </component>
      <transition name="delay-fade" :duration="2000">
        <img v-if="lazyPlaceholder"
             ref="placeholder"
             class="placeholder"
             role="presentation"
             :src="lazyPlaceholder"
             alt=""
        >
      </transition>
    </div>
  </div>
</template>

<style lang="stylus" scoped>
  $grow-factor = 1.02
  .delay-fade-enter-active
  .delay-fade-leave-active
    trans(opacity)
    transition-delay 2s
  .delay-fade-enter
  .delay-fade-leave-to /* .fade-leave-active below version 2.1.8 */ 
    opacity 0
    will-change opacity
    
  .ui-picture
    position relative
    overflow hidden
    // wrapper block so no extra gap for blank between next <tag>
    &.wrapper
      display block
      line-height 0
      // container inline so image+placeholder not force to take 100% width
      >.container
        display inline-block
        position relative
        overflow hidden
        size 100%
        text-align center 

    &[data-parallax="true"]
    &[data-parallax="true"][data-lazy="true"]&
      .picture .img
        transform scale(1.2)

    &[data-loaded="true"]
      .placeholder
        opacity 0
        transition-delay 500ms

    &:not([data-cover])
      .img
        height auto
    &[data-cover="none"]
      .img
        height auto
    &[data-cover="cover"]
      .picture
        size 100%
      .img
        object-fit cover

    &[data-cover="contain"]
      .placeholder
        position relative
      .picture
        display inline-block
        absolute 0
        size 100%
      .img
        object-fit contain
      &:not([data-placeholder]) .picture
        position static

    &[data-cover="cover"]
    &[data-cover="contain"]
      .img
        object-position v-bind(position)
        // transform-origin v-bind(position)
        // height auto
        // absolute 0
        size 100%

    &[data-cover="cover"]
      >.container
        absolute 0
      
      .picture
        .img
          absolute 0
          size 100%
    
    .img
      [data-lazy="true"]&
        display block 
        opacity 0
        transform scale($grow-factor)
        transition all 1.6s easing('out-quad') 0.2s
        [data-visible="true"][data-loaded="true"]&
          opacity 1
          transform scale(1.0)
          will-change opacity, transform
    
    .picture
    .img
      position relative
      display block
      z-index 2
      pointer-events none

    .placeholder
      absolute 0
      size 100%
      z-index 1
      transform scale($grow-factor)
      pointer-events none
      object-fit v-bind(cover)
      object-position v-bind(position)
      trans()
      opacity 0
      // transform-origin v-bind(position)

</style>
